import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import type {
  CreatePerformerDTO,
  EventKind,
  PerformerDTO,
  RoleFeeKind,
} from "../../domain/contracts";
import { ROLE_FEE_KIND_LABELS, RoleFeeKindSchema } from "../../domain/contracts";
import { performersKeys, useCreatePerformer } from "../api/queries";

const QuickCreateSchema = z.object({
  name: z.string().min(1, "ຕ້ອງປ້ອນຊື່ນັກສະແດງ"),
  phoneNumber: z.string().optional(),
  performerType: z.enum(["dancer", "choreographer", "kol", "other"]),
  defaultRoleFeeKind: RoleFeeKindSchema,
});

type QuickCreateValues = z.infer<typeof QuickCreateSchema>;

const ROLE_OPTIONS: Array<{ value: RoleFeeKind; label: string }> = (
  Object.entries(ROLE_FEE_KIND_LABELS) as Array<[RoleFeeKind, string]>
).map(([value, label]) => ({ value, label }));

function defaultsForEventKind(eventKind: EventKind): QuickCreateValues {
  if (eventKind === "kol") {
    return {
      name: "",
      phoneNumber: "",
      performerType: "kol",
      defaultRoleFeeKind: "performer",
    };
  }
  return {
    name: "",
    phoneNumber: "",
    performerType: "dancer",
    defaultRoleFeeKind: "performer",
  };
}

type QuickCreatePerformerPanelProps = {
  eventKind: EventKind;
  onCreated: (performer: PerformerDTO) => void;
  onCancel: () => void;
};

export function QuickCreatePerformerPanel({
  eventKind,
  onCreated,
  onCancel,
}: QuickCreatePerformerPanelProps) {
  const createPerformer = useCreatePerformer();
  const qc = useQueryClient();

  const methods = RHF.useForm<QuickCreateValues>({
    resolver: zodResolver(QuickCreateSchema),
    defaultValues: defaultsForEventKind(eventKind),
  });

  useEffect(() => {
    methods.reset(defaultsForEventKind(eventKind));
  }, [eventKind, methods]);

  return (
    <div className="rounded-md border border-dashed bg-muted/20 p-3">
      <p className="mb-3 font-medium text-sm">ເພີ່ມນັກສະແດງດ່ວນ</p>
      <FormRoot
        methods={methods}
        onSubmit={async (vals) => {
          const input: CreatePerformerDTO = {
            name: vals.name.trim(),
            phoneNumber: vals.phoneNumber?.trim()
              ? vals.phoneNumber.trim()
              : null,
            status: "active",
            performerType: vals.performerType,
            defaultRoleFeeKind: vals.defaultRoleFeeKind,
          };
          try {
            const created = await createPerformer.mutateAsync(input);
            await qc.invalidateQueries({
              queryKey: ["performance", "performers"],
            });
            await qc.invalidateQueries({ queryKey: performersKeys.all });
            onCreated(created);
          } catch {
            // toast handled by mutation
          }
        }}
        className="space-y-3"
      >
        <FormInput
          name="name"
          label="ຊື່ເຕັມ"
          placeholder="ຕົວຢ່າງ: ສົມສະໜຸກ ພົມມາວັນ"
          requiredMark
        />
        <FormInput
          name="phoneNumber"
          label="ເບີໂທ"
          placeholder="020xxxxxxxx (ບໍ່ບັງຄັບ)"
        />
        <FormSelect
          name="performerType"
          label="ປະເພດ"
          options={[
            { value: "dancer", label: "ນັກເຕັ້ນ" },
            { value: "choreographer", label: "Choreographer" },
            { value: "kol", label: "KOL" },
            { value: "other", label: "ອື່ນໆ" },
          ]}
        />
        <FormSelect
          name="defaultRoleFeeKind"
          label="ບົດບາດເລີ່ມຕົ້ນ"
          options={ROLE_OPTIONS}
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
          <Button type="submit" size="sm" disabled={createPerformer.isPending}>
            {createPerformer.isPending ? "ກຳລັງສ້າງ..." : "ສ້າງ ແລະ ເລືອກ"}
          </Button>
        </div>
      </FormRoot>
    </div>
  );
}
